// src/pages/admin/components/SpecializedUnitsPanel.jsx

import React from 'react';
import { AdminAPI } from '../../../api/admin';
import { Card, Actions, Btn, Field, Row2, Row3 } from '../ui';

export default function SpecializedUnitsPanel({ propertyId, propertyType }) {
  const [units, setUnits] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [showForm, setShowForm] = React.useState(false);
  const [editingUnit, setEditingUnit] = React.useState(null);

  // Определяем API методы в зависимости от типа объекта
  const getApiMethods = () => {
    switch (propertyType) {
      case 'foreign':
        return {
          list: AdminAPI.listForeignUnits,
          create: AdminAPI.createForeignUnit,
        };
      case 'suburban':
        return {
          list: AdminAPI.listSuburbanUnits,
          create: AdminAPI.createSuburbanUnit,
        };
      case 'commercial-rent':
        return {
          list: AdminAPI.listCommercialRentUnits,
          create: AdminAPI.createCommercialRentUnit,
        };
      case 'parking':
        return {
          list: AdminAPI.listParkingUnits,
          create: AdminAPI.createParkingUnit,
        };
      default:
        return {
          list: AdminAPI.listUnits,
          create: AdminAPI.createUnit,
        };
    }
  };

  const api = React.useMemo(() => getApiMethods(), [propertyType]);

  const [form, setForm] = React.useState({
    unit_number: '',
    bedrooms: '',
    bathrooms: '',
    area_total: '',
    area_living: '',
    price: '',
    price_currency: 'AED',
    floor: '',
    status: 'available',
    is_available: true,
    description: '',
  });

  const load = React.useCallback(async () => {
    if (busy) return; // Предотвращаем повторные запросы
    
    setBusy(true);
    setMsg('');
    try {
      const resp = await api.list(propertyId);
      setUnits(Array.isArray(resp?.units) ? resp.units : []);
    } catch (e) {
      console.error('Error loading units:', e);
      setMsg(e.message || 'Не удалось загрузить планировки');
    } finally {
      setBusy(false);
    }
  }, [propertyId, propertyType, busy]);

  React.useEffect(() => { 
    if (propertyId) {
      load(); 
    }
  }, [propertyId, propertyType]); // Убираем load из зависимостей

  const resetForm = () => {
    setForm({
      unit_number: '',
      bedrooms: '',
      bathrooms: '',
      area_total: '',
      area_living: '',
      price: '',
      price_currency: 'AED',
      floor: '',
      status: 'available',
      is_available: true,
      description: '',
    });
    setEditingUnit(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.unit_number.trim()) {
      setMsg('Укажите номер юнита');
      return;
    }

    setBusy(true);
    try {
      const payload = {
        ...form,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : undefined,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : undefined,
        area_total: form.area_total ? Number(form.area_total) : undefined,
        area_living: form.area_living ? Number(form.area_living) : undefined,
        price: form.price ? Number(form.price) : undefined,
        floor: form.floor ? Number(form.floor) : undefined,
      };

      if (editingUnit) {
        await AdminAPI.updateUnit(editingUnit.id, payload);
        setMsg('Юнит обновлен');
      } else {
        await api.create(propertyId, payload);
        setMsg('Юнит создан');
      }

      await load();
      resetForm();
      setShowForm(false);
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка сохранения юнита');
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (unit) => {
    setForm({
      unit_number: unit.unit_number || unit.unit_no || '',
      bedrooms: unit.bedrooms || '',
      bathrooms: unit.bathrooms || '',
      area_total: unit.area_total || '',
      area_living: unit.area_living || '',
      price: unit.price || '',
      price_currency: unit.price_currency || 'AED',
      floor: unit.floor || '',
      status: unit.status || 'available',
      is_available: unit.is_available ?? true,
      description: unit.description || '',
    });
    setEditingUnit(unit);
    setShowForm(true);
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Удалить юнит?')) return;
    setBusy(true);
    try {
      await AdminAPI.deleteUnit(unitId);
      await load();
      setMsg('Юнит удален');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg(e.message || 'Ошибка удаления юнита');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Планировки ({units.length})</h4>
        <Actions style={{ margin: 0 }}>
          <Btn onClick={() => setShowForm(!showForm)} disabled={busy}>
            {showForm ? 'Отмена' : '+ Добавить юнит'}
          </Btn>
        </Actions>
      </div>

      {msg && <div style={{ color: msg.includes('Ошибка') ? '#e66' : '#1b5e20', marginBottom: 8 }}>{msg}</div>}

      {showForm && (
        <Card style={{ marginBottom: 16, background: '#f8f9fa' }}>
          <h5>{editingUnit ? 'Редактировать юнит' : 'Новый юнит'}</h5>
          <form onSubmit={handleSubmit}>
            <Row2>
              <Field>
                Номер юнита *
                <input
                  value={form.unit_number}
                  onChange={(e) => setForm(f => ({ ...f, unit_number: e.target.value }))}
                  placeholder="A-101"
                  required
                />
              </Field>
              <Field>
                Статус
                <select
                  value={form.status}
                  onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="available">Доступен</option>
                  <option value="reserved">Забронирован</option>
                  <option value="sold">Продан</option>
                </select>
              </Field>
            </Row2>

            <Row3>
              <Field>
                Комнаты
                <input
                  type="number"
                  value={form.bedrooms}
                  onChange={(e) => setForm(f => ({ ...f, bedrooms: e.target.value }))}
                  placeholder="1"
                />
              </Field>
              <Field>
                Санузлы
                <input
                  type="number"
                  value={form.bathrooms}
                  onChange={(e) => setForm(f => ({ ...f, bathrooms: e.target.value }))}
                  placeholder="1"
                />
              </Field>
              <Field>
                Этаж
                <input
                  type="number"
                  value={form.floor}
                  onChange={(e) => setForm(f => ({ ...f, floor: e.target.value }))}
                  placeholder="5"
                />
              </Field>
            </Row3>

            <Row3>
              <Field>
                Общая площадь, м²
                <input
                  type="number"
                  step="0.1"
                  value={form.area_total}
                  onChange={(e) => setForm(f => ({ ...f, area_total: e.target.value }))}
                  placeholder="65.5"
                />
              </Field>
              <Field>
                Жилая площадь, м²
                <input
                  type="number"
                  step="0.1"
                  value={form.area_living}
                  onChange={(e) => setForm(f => ({ ...f, area_living: e.target.value }))}
                  placeholder="45.2"
                />
              </Field>
              <Field>
                Цена
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))}
                  placeholder="1500000"
                />
              </Field>
            </Row3>

            <Row2>
              <Field>
                Валюта
                <select
                  value={form.price_currency}
                  onChange={(e) => setForm(f => ({ ...f, price_currency: e.target.value }))}
                >
                  <option value="AED">AED</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="RUB">RUB</option>
                </select>
              </Field>
              <Field>
                Доступен
                <select
                  value={form.is_available}
                  onChange={(e) => setForm(f => ({ ...f, is_available: e.target.value === 'true' }))}
                >
                  <option value="true">Да</option>
                  <option value="false">Нет</option>
                </select>
              </Field>
            </Row2>

            <Field>
              Описание
              <textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Дополнительная информация о юните..."
              />
            </Field>

            <Actions>
              <Btn type="submit" $kind="primary" disabled={busy}>
                {editingUnit ? 'Сохранить' : 'Создать'}
              </Btn>
              <Btn type="button" onClick={() => { resetForm(); setShowForm(false); }}>
                Отмена
              </Btn>
            </Actions>
          </form>
        </Card>
      )}

      {busy && <div style={{ opacity: 0.6 }}>Загрузка...</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {units.map((unit) => (
          <Card key={unit.id} style={{ background: '#f8f9fa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <h5 style={{ margin: '0 0 8px 0' }}>
                  {(() => {
                    // Пробуем разные варианты названия
                    const title = unit.title_ru || unit.title_en || unit.title;
                    const number = unit.unit_number || unit.unit_no;
                    
                    if (title) return title;
                    if (number) return `Юнит ${number}`;
                    return `Юнит #${unit.id}`;
                  })()}
                  {unit.unit_number && ` (${unit.unit_number})`}
                  {unit.unit_no && unit.unit_no !== unit.unit_number && ` [${unit.unit_no}]`}
                  {!unit.is_available && <span style={{ color: '#e66', marginLeft: 8 }}>• Недоступен</span>}
                </h5>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, fontSize: 13, color: '#666' }}>
                  {unit.bedrooms && <div>🛏 {unit.bedrooms} комн.</div>}
                  {unit.bathrooms && <div>🚿 {unit.bathrooms} с/у</div>}
                  {unit.area_total && <div>📐 {unit.area_total} м²</div>}
                  {unit.floor && <div>🏢 {unit.floor} этаж</div>}
                  {unit.price && <div>💰 {unit.price} {unit.price_currency || 'AED'}</div>}
                  {unit.status && <div>📋 {unit.status}</div>}
                  <div style={{ fontSize: 11, color: '#999' }}>ID: {unit.id}</div>
                </div>
                
                {/* Отладочная информация - показываем все поля */}
                <details style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
                  <summary>Отладка (все поля)</summary>
                  <pre style={{ fontSize: 10, background: '#f0f0f0', padding: 4, marginTop: 4 }}>
                    {JSON.stringify(unit, null, 2)}
                  </pre>
                </details>
                
                {unit.description && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#777' }}>
                    {unit.description}
                  </div>
                )}
              </div>
              
              <Actions style={{ margin: 0 }}>
                <Btn onClick={() => handleEdit(unit)} disabled={busy}>
                  Редактировать
                </Btn>
                <Btn onClick={() => handleDelete(unit.id)} disabled={busy} style={{ color: '#e66' }}>
                  Удалить
                </Btn>
              </Actions>
            </div>
          </Card>
        ))}
      </div>

      {units.length === 0 && !busy && (
        <div style={{ textAlign: 'center', padding: 20, color: '#777' }}>
          Планировки не добавлены. Нажмите "Добавить юнит" для создания.
        </div>
      )}
    </Card>
  );
}